import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';
import {
  StudentModel,
  StudentRequest,
  UserRequest,
} from '@generativ/much-smarter-models';
import { JwtHelperService } from '@auth0/angular-jwt';

import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from '../services/error-handler.service';
import { Intercom } from '../intercom/intercom.service';
import { PaymentService } from './payment.service';

export interface EmbeddedSignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface Auth0SignUpResponse {
  _id: string;
  email: string;
  email_verified: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  currentStudent: StudentModel;
  public token: string;
  jwtHelper = new JwtHelperService();
  tokenIdentifier = 'id_token';
  currentPageLogin = false;
  lastLogin;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private intercom: Intercom,
    private paymentService: PaymentService
  ) {
    this.token = localStorage.getItem('tokenId');
    this.lastLogin = localStorage.getItem('last-login');
    const stringStudent = localStorage.getItem('current-student');
    if (stringStudent) {
      this.currentStudent = new StudentModel().parse(JSON.parse(stringStudent));
    }
  }

  isAuthenticated() {
    if (
      localStorage.getItem('tokenId') &&
      this.jwtHelper.isTokenExpired(this.token)
    ) {
      this.logout();
    }
    return !this.jwtHelper.isTokenExpired(this.token);
  }

  getAuthorizationHeader(): string {
    return this.token ? this.token : '';
  }

  signIn(userInfo: UserRequest.Login): Observable<string> {
    const url = environment.auth0.loginUrl;

    const body = {
      client_id: environment.auth0.clientID,
      audience: environment.auth0.audience,
      realm: environment.auth0.realm,
      grant_type: environment.auth0.grant_type,
      username: userInfo.email,
      password: userInfo.password,
      scope: 'openid profile email',
    };

    return this.http
      .post(url, body, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((authResult) => {
          if (authResult && authResult[this.tokenIdentifier]) {
            const token = authResult[this.tokenIdentifier];
            localStorage.setItem('tokenId', token);
            this.token = token;
            this.setToken().subscribe();
            const decodedJson = this.jwtHelper.decodeToken(
              authResult[this.tokenIdentifier]
            );
            // Todo: Save user info.
            return decodedJson.email;
          }
        }),
        catchError(this.errorHandlerService.handleAuth0Error)
      );
  }

  signUp(model: StudentRequest.Create) {
    const url = environment.auth0.signUpUrl;
    const scores = {
      currentScoreEnglish: model.currentScoreEnglish,
      currentScoreMath: model.currentScoreMath,
      currentScoreReading: model.currentScoreReading,
      currentScoreScience: model.currentScoreScience,
      goalScoreEnglish: model.goalScoreEnglish,
      goalScoreMath: model.goalScoreMath,
      goalScoreReading: model.goalScoreReading,
      goalScoreScience: model.goalScoreScience,
    };
    const body = {
      client_id: environment.auth0.clientID,
      connection: environment.auth0.realm,
      email: model.email,
      password: model.password,
      user_metadata: {
        firstName: model.firstName,
        lastName: model.lastName,
        phoneNumber: model.phoneNumber,
        scores: JSON.stringify(scores),
        accessCode: model.accessCode,
        graduationYear: model.graduationYear,
        birthdate: model.birthdate,
      },
    };
    return this.http
      .post(url, body, { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((response: Response) => response),
        catchError(this.errorHandlerService.handleAuth0Error)
      );
  }

  signUpEmbedded(model: EmbeddedSignUpRequest): Observable<Auth0SignUpResponse> {
    const body = {
      client_id: environment.auth0.clientID,
      connection: environment.auth0.realm,
      email: model.email,
      password: model.password,
      user_metadata: {
        firstName: model.firstName,
        lastName: model.lastName,
        phoneNumber: model.phoneNumber,
      },
    };

    return this.http
      .post<Auth0SignUpResponse>(environment.auth0.signUpUrl, body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(this.errorHandlerService.handleAuth0Error));
  }

  forgotPassword(email: string): Observable<any> {
    const body = {
      client_id: environment.auth0.clientID,
      email,
      connection: environment.auth0.realm,
    };

    return this.http
      .post(environment.auth0.resetPasswordUrl, body, {
        headers: { 'content-type': 'application/json' },
      })
      .pipe(
        map((res: Response) => res),
        catchError((err) => {
          if (err.status === 200) {
            return of(err);
          }
          return this.errorHandlerService.handleAuth0Error(err);
        })
      );
  }

  logout(): void {
    this.paymentService.status = null;
    const url = window.location.protocol + '//' + window.location.hostname;
    const port =
      window.location.port.length > 0 ? ':' + window.location.port : '';
    const redirectUrl = encodeURIComponent(url) + port;
    window.location.href =
      environment.auth0.logoutUrl +
      redirectUrl +
      '/&client_id=' +
      environment.auth0.clientID;
    // Clear token remove user from local storage to log user out
    this.token = null;
    localStorage.removeItem('tokenId');
    localStorage.removeItem('current-student');
    localStorage.clear();
    this.intercom.shutdown();
    this.intercom.boot({
      app_id: environment.intercom.appId,
      widget: {
        activator: '#intercom',
      },
    });
  }

  checkAccessCode(accessCode: string): Observable<boolean> {
    return this.http.post<boolean>(
      environment.msStudentApiUrl + 'auth/accessCodeCheck',
      accessCode
    );
  }

  getToken(): Observable<any> {
    return this.http.get(environment.msStudentApiUrl + 'auth/token', {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  setToken() {
    console.log('set token');
    return this.http.post(
      environment.msStudentApiUrl + 'auth/token',
      {},
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  setCurrentStudent(student: StudentModel): void {
    this.currentStudent = student;
    localStorage.setItem('current-student', JSON.stringify(student));
    if (this.lastLogin !== student.email) {
      // localStorage.removeItem('selectedCountry');
      localStorage.removeItem('selectedCourse');
    }
    localStorage.setItem('last-login', student.email);
  }

  addKeapContact(phoneNumber: string): Observable<StudentModel> {
    const params = Object.fromEntries(new URLSearchParams(window.location.search));
    return this.http.post<StudentModel>(
      environment.msStudentApiUrl + 'keap/create',
      { phoneNumber, ...params }
    );
  }

  addDemographicTags(tagNames: string[]): Observable<StudentModel> {
    return this.http.post<StudentModel>(
      environment.msStudentApiUrl + 'keap/demographics',
      tagNames
    );
  }
}
